import posthogEvents from "@app/config/posthog/posthog-events";
import UploadDatasetModal from "@app/routes/dataset/upload/UploadDatasetModal";
import ModalHeader from "@lib/common/components/modals/ModalHeader";
import { notifications } from "@lib/notification/notification";
import { Modal } from "@mantine/core";
import type { DropzoneProps } from "@mantine/dropzone";
import { Dropzone, type FileWithPath } from "@mantine/dropzone";
import { usePostHog } from "posthog-js/react";
import { useCallback, useState } from "react";

export function BaseDatasetDropzone({
  maxSize = 5 * 1024 * 1024,
  accept = { "text/csv": [".csv", ".Csv", ".CSV"], "text/plain": [".txt", ".Txt", ".TXT"] },
  multiple = false,
  children,
  ...props
}: Omit<DropzoneProps, "onDrop">) {
  const posthog = usePostHog();
  const [inputFiles, setInputFiles] = useState<FileWithPath[]>([]);

  const closeModal = useCallback(() => setInputFiles([]), []);

  const onDropCallback = (files: FileWithPath[]) => {
    if (files.length <= 0) {
      notifications.error("No dataset was selected.");
      return;
    }

    if (files.length > 1) {
      notifications.error("You can only upload one dataset at a time.");
      return;
    }

    posthog.capture(posthogEvents.dataset_drop_zone.file_upload);
    setInputFiles(files);
  };

  return (
    <>
      <Dropzone
        onDrop={onDropCallback}
        onReject={() =>
          notifications.error("Files uploaded must have a .csv or .txt extension, and a max size of 10MB")
        }
        maxSize={maxSize}
        accept={accept}
        multiple={multiple}
        {...props}
      >
        {children}
      </Dropzone>

      {inputFiles?.length > 0 && (
        <Modal
          size={"auto"}
          opened={Boolean(inputFiles)}
          onClose={closeModal}
          withCloseButton={false}
          closeOnEscape={false}
          withOverlay
        >
          <ModalHeader title="Create dataset" onClose={closeModal}></ModalHeader>
          <UploadDatasetModal file={inputFiles[0]} onClose={closeModal} />
        </Modal>
      )}
    </>
  );
}
